/**
 * Adapts flat GET /calendar/cards `cards[]` into the nested bookingsList shape
 * Home / sockets / sidebars already consume (booking_item keyed by pax name).
 */

const toFlag01 = (value) => {
	if (value === true || value === 1 || value === "1") return 1;
	if (value === false || value === 0 || value === "0") return 0;
	if (value == null || value === "") return 0;
	return Number(value) ? 1 : 0;
};

const toOptionalFlag01OrNull = (value) => {
	if (value === true || value === 1 || value === "1") return 1;
	if (value === false || value === 0 || value === "0") return 0;
	if (value == null || value === "") return null;
	return Number(value) ? 1 : 0;
};

/**
 * Build local `YYYY-MM-DD HH:mm:ss` for Home parsers that split on space.
 * Prefer calendar_date + start_time (already outlet-local) over UTC ISO service_at.
 */
const buildServiceAtLocal = (card) => {
	const calendarDate = card?.calendar_date;
	let startTime = card?.start_time;
	if (calendarDate && startTime) {
		const parts = String(startTime).split(":");
		const hh = String(Number(parts[0]) || 0).padStart(2, "0");
		const mm = String(Number(parts[1]) || 0).padStart(2, "0");
		const ss = String(Number(parts[2]) || 0).padStart(2, "0");
		return `${calendarDate} ${hh}:${mm}:${ss}`;
	}

	const serviceAt = card?.service_at;
	if (typeof serviceAt === "string" && serviceAt.includes("T")) {
		// Fallback: strip offset and use wall-clock from ISO date part + start_time if present
		if (startTime) {
			const datePart = serviceAt.slice(0, 10);
			const parts = String(startTime).split(":");
			const hh = String(Number(parts[0]) || 0).padStart(2, "0");
			const mm = String(Number(parts[1]) || 0).padStart(2, "0");
			const ss = String(Number(parts[2]) || 0).padStart(2, "0");
			return `${datePart} ${hh}:${mm}:${ss}`;
		}
		return serviceAt.replace("T", " ").replace(/\+.*$/, "").replace(/Z$/, "").slice(0, 19);
	}

	if (typeof serviceAt === "string" && serviceAt.includes(" ")) {
		return serviceAt.slice(0, 19);
	}

	return serviceAt || null;
};

const mapRoomItems = (roomItems) => {
	if (!Array.isArray(roomItems)) return [];
	return roomItems.map((room) => ({
		...room,
		// Home falls back to room_id when item_id is absent
		item_id: room?.item_id ?? room?.room_id,
		room_id: room?.room_id,
		room_name: room?.room_name,
		room_type: room?.room_type,
		facilities: room?.facilities ?? [],
		duration: room?.duration,
		start_time: room?.start_time,
		end_time: room?.end_time,
		priority: room?.priority ?? null,
		id: room?.id,
	}));
};

const mapCardToBookingItem = (card) => {
	const duration = Number(card?.duration_minutes ?? card?.duration) || 0;
	const bookingStatus = card?.booking_status ?? card?.status ?? null;
	const isHolding = String(bookingStatus || "").toLowerCase() === "holding";
	const crossDay = Array.isArray(card?.cross_day) && card.cross_day.length > 0
		? card.cross_day
		: null;

	const primaryRaw = card?.primary;
	const primary =
		primaryRaw === true || primaryRaw === 1 || primaryRaw === "1"
			? 1
			: primaryRaw === false || primaryRaw === 0 || primaryRaw === "0"
				? 0
				: primaryRaw == null
					? null
					: toOptionalFlag01OrNull(primaryRaw);

	return {
		id: card?.booking_item_id ?? card?.id,
		booking_id: card?.booking_id,
		therapist_id: card?.therapist_id ?? null,
		therapist: card?.therapist ?? card?.therapist_name ?? "",
		therapist_name: card?.therapist_name ?? card?.therapist ?? "",
		therapist_pager_number: card?.therapist_pager_number ?? card?.pager_number ?? "",
		therapist_gender: card?.therapist_gender ?? card?.gender ?? "",
		parent_booking_item_id: card?.parent_booking_item_id ?? null,
		service: card?.service_name ?? card?.service ?? "",
		service_name: card?.service_name ?? card?.service ?? "",
		service_id: card?.service_id ?? null,
		service_category: card?.service_category_name ?? card?.service_category ?? "",
		service_category_name: card?.service_category_name ?? card?.service_category ?? "",
		// Holding UI keys off item.status === "Holding"; otherwise keep numeric item_status
		status: isHolding ? "Holding" : (card?.item_status ?? card?.status ?? 1),
		start_time: card?.start_time ?? null,
		end_time: card?.end_time ?? null,
		service_at: buildServiceAtLocal(card),
		duration,
		totalDuration: duration,
		is_clawback: card?.is_clawback ?? false,
		room_items: mapRoomItems(card?.room_items),
		addons_total: card?.addons_total ?? 0,
		is_calendar_expansion: toFlag01(card?.is_calendar_expansion),
		addons: card?.addons ?? [],
		customer_name: card?.pax_name ?? card?.customer_name ?? "",
		requested_person: toFlag01(card?.requested_person),
		requested_room: toFlag01(card?.requested_room),
		service_request: card?.service_request ?? "",
		primary,
		item_number: card?.item_number ?? null,
		splited: card?.splited ?? false,
		cross_day_booking_item: crossDay,
		isMemberRateApplied: card?.isMemberRateApplied ?? false,
		isMembershipApplied: card?.isMembershipApplied ?? false,
		time_range: card?.time_range ?? null,
		service_date: card?.service_date ?? null,
		service_time: card?.service_time ?? null,
		calendar_date: card?.calendar_date ?? null,
		service_end: card?.service_end ?? null,
	};
};

/**
 * @param {Array<Object>} cards
 * @returns {Array<Object>} nested bookings compatible with Home bookingsList
 */
export const adaptCalendarCardsToBookingsList = (cards) => {
	if (!Array.isArray(cards) || cards.length === 0) return [];

	const byBookingId = new Map();

	cards.forEach((card) => {
		const bookingId = card?.booking_id;
		if (bookingId == null) return;

		const paxKey = card?.pax_name || card?.customer_name || "Guest";
		const item = mapCardToBookingItem(card);

		if (!byBookingId.has(bookingId)) {
			const paymentStatus = card?.payment_status ?? null;
			byBookingId.set(bookingId, {
				id: bookingId,
				sale_id: card?.sale_id ?? null,
				sale: paymentStatus != null
					? { payment_status: paymentStatus }
					: null,
				payment_status: paymentStatus,
				company_id: card?.company_id ?? null,
				booking_type_id: card?.booking_type_id ?? null,
				outlet_id: card?.outlet_id ?? null,
				user_id: card?.user_id ?? null,
				membership: card?.membership ?? null,
				note: card?.notes ?? card?.note ?? null,
				notes: card?.notes ?? null,
				web_notes: card?.web_notes ?? null,
				source: card?.source ?? null,
				status: card?.booking_status ?? card?.status ?? null,
				is_recurring_parent: card?.is_recurring_parent ?? 0,
				is_recurring: Boolean(card?.is_recurring),
				panel: card?.panel ?? null,
				mobile_number: card?.mobile_number ?? null,
				customer_name: card?.customer_name ?? null,
				created_by: card?.created_by ?? null,
				created_by_name: card?.created_by_name ?? card?.user_name ?? null,
				user_name: card?.created_by_name ?? card?.user_name ?? null,
				service_date: card?.service_date ?? null,
				service_time: card?.service_time ?? null,
				expires_at: card?.expires_at ?? null,
				booking_item: {},
			});
		}

		const booking = byBookingId.get(bookingId);

		// Prefer non-null booking-level fields from any card in the group
		if (booking.booking_type_id == null && card?.booking_type_id != null) {
			booking.booking_type_id = card.booking_type_id;
		}
		if (!booking.is_recurring && card?.is_recurring) {
			booking.is_recurring = true;
		}
		if (booking.payment_status == null && card?.payment_status != null) {
			booking.payment_status = card.payment_status;
			booking.sale = { payment_status: card.payment_status };
		}
		if (!booking.note && (card?.notes || card?.note)) {
			booking.note = card.notes ?? card.note;
			booking.notes = card.notes ?? null;
		}
		if (!booking.customer_name && card?.customer_name) {
			booking.customer_name = card.customer_name;
		}
		if (!booking.mobile_number && card?.mobile_number) {
			booking.mobile_number = card.mobile_number;
		}
		// Prefer primary card's booking status when present
		if (card?.primary === true || card?.primary === 1 || card?.primary === "1") {
			if (card?.booking_status || card?.status) {
				booking.status = card.booking_status ?? card.status;
			}
		}

		if (!booking.booking_item[paxKey]) {
			booking.booking_item[paxKey] = [];
		}
		booking.booking_item[paxKey].push(item);
	});

	return Array.from(byBookingId.values()).map((booking) => {
		const paxKeys = Object.keys(booking.booking_item || {});
		// Infer group booking when backend leaves booking_type_id null but multiple pax keys exist
		if (booking.booking_type_id == null && paxKeys.length > 1) {
			booking.booking_type_id = 2;
		}
		return booking;
	});
};

export default adaptCalendarCardsToBookingsList;
